import React, { useEffect, useState, useRef, KeyboardEvent } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from './index';
import { ChevronDown } from 'lucide-react';

const cn = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

// Check if we're in development mode
const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';

const LANGS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  // Gibberish mode only in development - helps catch hardcoded strings
  ...(isDev ? [{ value: 'xx', label: '🔧 Gibberish (Debug)' }] : [])
];

const LanguageSwitcher: React.FC<{ className?: string }> = ({ className }) => {
  const { locale, setLocale, register } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // register built-in locales
    import('./locales/en.json').then(m => register('en', m));
    import('./locales/es.json').then(m => register('es', m));
  }, [register]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!menuRef.current || !inputRef.current) return;
      if (e.target instanceof Node && !menuRef.current.contains(e.target) && !inputRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const filtered = LANGS.filter(l => l.label.toLowerCase().includes(query.toLowerCase()));

  const onInputKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && activeIndex >= 0) {
        const sel = filtered[activeIndex];
        if (sel) selectValue(sel.value);
      } else {
        setOpen(o => !o);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const selectValue = (val: string) => {
    setLocale(val);
    setQuery('');
    setOpen(false);
    setActiveIndex(-1);
    inputRef.current?.blur();
  };

  return (
    <div className={cn('relative inline-block', className)}>
      <div className="flex items-center border rounded-md bg-background">
        <input
          ref={inputRef}
          className="px-3 py-2 w-36 bg-transparent outline-none text-sm"
          placeholder="Select language..."
          value={open ? query : (LANGS.find(l => l.value === locale)?.label ?? locale)}
          onFocus={() => setOpen(true)}
          onChange={e => { setQuery(e.target.value); setActiveIndex(-1); setOpen(true); }}
          onKeyDown={onInputKey}
          aria-expanded={open}
          aria-controls="language-listbox"
          aria-autocomplete="list"
          aria-haspopup="listbox"
        />
        <button
          onClick={() => { setOpen(o => !o); inputRef.current?.focus(); }}
          className="px-2"
          aria-label="Toggle languages"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={menuRef}
            id="language-listbox"
            initial={{ opacity: 0, scale: 0.95, y: -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -6 }}
            transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            className="absolute right-0 mt-2 w-40 rounded-md border bg-popover shadow-lg z-50"
            role="listbox"
          >
            <div className="py-1">
              {filtered.length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">No results</div>
              )}
              {filtered.map((l, idx) => (
                <div
                  key={l.value}
                  role="option"
                  aria-selected={l.value === locale}
                  onMouseDown={e => e.preventDefault()} // prevent blur
                  onClick={() => selectValue(l.value)}
                  className={cn(
                    'w-full text-left px-3 py-2 text-sm cursor-pointer hover:bg-accent/50',
                    idx === activeIndex && 'bg-accent/30',
                    l.value === locale && 'font-semibold'
                  )}
                >
                  {l.label}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;
